import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useGetLobbyBannersQuery } from "../../../store/service/lobbyBannerService";
import { useGetMatchesBySportQuery } from "../../../store/service/clientBetService";
import { formatOdd } from "../../pages/client/clientUtils";
import { APP_BRAND_MARK, APP_BRAND_TAIL } from "../../../utils/brand";
import {
  ALL_SPORTS_MENU,
  FEATURED_EVENTS,
  LobbySportIcon,
  QUICK_TAB_ITEMS,
  SPORT_STRIP_ITEMS,
  TOP_NAV_ITEMS,
} from "../../pages/client/lobbySportIcons";
import "../../pages/client/Client.scss";

const LOBBY_IMG = "/Images/lobby";

const FALLBACK_BANNERS = [
  `${LOBBY_IMG}/1784145560718.webp`,
  `${LOBBY_IMG}/1784175872234.webp`,
];

const FALLBACK_TRENDING = [
  { imageUrl: `${LOBBY_IMG}/1778317315739.webp`, altText: "Super Over 2" },
  { imageUrl: `${LOBBY_IMG}/1778317145379.webp`, altText: "Andar Bahar" },
  { imageUrl: `${LOBBY_IMG}/1778317091657.webp`, altText: "Mini Super Over" },
];

const FALLBACK_CASINO = [
  { imageUrl: `${LOBBY_IMG}/1778317091657.webp`, altText: "Mini Super Over" },
  { imageUrl: `${LOBBY_IMG}/1778317109540.webp`, altText: "Unique Roulette" },
];

const FALLBACK_NEW_GAMES = [
  { imageUrl: `${LOBBY_IMG}/1778317073097.webp`, altText: "Pilot Chicken" },
  { imageUrl: `${LOBBY_IMG}/1778318146775.webp`, altText: "JetX" },
];

const WINNERS = [
  { name: "Ph*****", amount: "1,22,15,000", time: "15/07/2026 10:36" },
  { name: "Kd*****", amount: "1,17,20,500", time: "15/07/2026 10:31" },
  { name: "Ma*****", amount: "1,00,27,500", time: "15/07/2026 10:24" },
  { name: "Ra*****", amount: "88,50,000", time: "15/07/2026 10:18" },
  { name: "Vi*****", amount: "72,10,250", time: "15/07/2026 10:12" },
];

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-7-2a2 2 0 1 1 4 0v2h-4V7zm7 12H7v-8h10v8z" />
  </svg>
);

const TvIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M3 6h18v12H3V6zm2 2v8h14V8H5zm5 12h4v2H10v-2z" />
  </svg>
);

const OddCell = ({ back, lay, suspended }) => {
  if (suspended || !(Number(back) > 0 || Number(lay) > 0)) {
    return (
      <div className="w777-odd-pair is-locked">
        <span className="w777-odd-back">
          <LockIcon />
        </span>
        <span className="w777-odd-lay">
          <LockIcon />
        </span>
      </div>
    );
  }
  return (
    <div className="w777-odd-pair">
      <span className="w777-odd-back">{formatOdd(back)}</span>
      <span className="w777-odd-lay">{formatOdd(lay)}</span>
    </div>
  );
};

const PreLoginLobby = ({
  loginOpen,
  onOpenLogin,
  onCloseLogin,
  loginModal,
  headerLogin,
  footer,
}) => {
  const { data: lobbyData } = useGetLobbyBannersQuery();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [activeSport, setActiveSport] = useState(SPORT_STRIP_ITEMS[0]);
  const [topNav, setTopNav] = useState("Exchange");

  const sportId = activeSport?.sportId || 4;
  const { data: matchData, isLoading: matchesLoading } = useGetMatchesBySportQuery(sportId, {
    pollingInterval: 5000,
    skip: !activeSport?.sportId,
  });

  const lobby = lobbyData?.data || {};

  const banners = useMemo(() => {
    const rows = lobby.banner || [];
    if (!rows.length) return FALLBACK_BANNERS;
    return rows.map((row) => row.imageUrl);
  }, [lobby.banner]);

  const trendingCards = lobby.trending?.length ? lobby.trending : FALLBACK_TRENDING;
  const casinoCards = lobby.casino?.length ? lobby.casino : FALLBACK_CASINO;
  const newGameCards = lobby.new_games?.length ? lobby.new_games : FALLBACK_NEW_GAMES;

  const matches = useMemo(() => {
    const list = [...(matchData?.data || [])];
    list.sort((a, b) => Number(b.inPlay) - Number(a.inPlay) || moment(a.openDate).valueOf() - moment(b.openDate).valueOf());
    return list.slice(0, 40);
  }, [matchData]);

  const fixtures = useMemo(() => matches.slice(0, 8), [matches]);

  useEffect(() => {
    setBannerIdx(0);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = setInterval(() => {
      setBannerIdx((idx) => (idx + 1) % banners.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [banners.length]);

  const renderCards = (cards) =>
    cards.map((card) => (
      <article
        key={`${card.id || card.altText}-${card.imageUrl}`}
        className="w777-card w777-card-img"
        style={{ backgroundImage: `url(${card.imageUrl})` }}
        aria-label={card.altText}
      />
    ));

  const leftRail = (
    <>
      <div className="w777-rail-block">
        <h4 className="w777-rail-title">Upcoming Fixtures</h4>
        <div className="w777-fixture-list">
          {fixtures.length === 0 ? (
            <p className="w777-rail-empty">Fixtures loading…</p>
          ) : (
            fixtures.map((m) => (
              <button
                key={m.matchId}
                type="button"
                className="w777-fixture-row"
                onClick={onOpenLogin}>
                <span className="w777-fixture-name">{m.matchName}</span>
                <span className="w777-fixture-time">
                  {m.inPlay ? "Live" : moment(m.openDate).format("DD MMM HH:mm")}
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="w777-rail-block">
        <h4 className="w777-rail-title">Racing Sports</h4>
        <div className="w777-sport-menu">
          <button type="button" onClick={onOpenLogin}>
            Horse Racing
          </button>
          <button type="button" onClick={onOpenLogin}>
            Greyhound Racing
          </button>
        </div>
      </div>

      <div className="w777-rail-block">
        <h4 className="w777-rail-title">All Sports</h4>
        <div className="w777-sport-menu">
          {ALL_SPORTS_MENU.map((s) => (
            <button
              key={s.label}
              type="button"
              className={activeSport?.label === s.label ? "active" : ""}
              onClick={() => {
                if (s.sportId) {
                  setActiveSport({ label: s.label, icon: s.icon, sportId: s.sportId });
                }
                setMenuOpen(false);
              }}>
              <LobbySportIcon name={s.icon} className="w777-menu-glyph" />
              <span>
                {s.label}
                <em>({s.count})</em>
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="w777-login-page">
      <nav className="w777-top-nav" aria-label="Product menu">
        <div className="w777-top-nav-inner">
          {TOP_NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              type="button"
              className={topNav === item.label || item.active ? "active" : ""}
              onClick={() => {
                setTopNav(item.label);
                onOpenLogin();
              }}>
              <LobbySportIcon name={item.icon} className="w777-top-nav-icon" />
              <span>{item.label}</span>
            </button>
          ))}
          <button type="button" className="w777-top-nav-gear" aria-label="Settings" onClick={onOpenLogin}>
            ⚙
          </button>
        </div>
      </nav>

      {menuOpen ? (
        <button className="w777-drawer-backdrop" onClick={() => setMenuOpen(false)} />
      ) : null}
      <aside className={`w777-side-drawer ${menuOpen ? "open" : ""}`}>
        <div className="w777-side-head">
          <h3>
            {APP_BRAND_MARK} <small>{APP_BRAND_TAIL}</small>
          </h3>
        </div>
        <div className="w777-side-scroll">{leftRail}</div>
      </aside>

      <header className="w777-login-header">
        <button
          type="button"
          className="w777-login-menu"
          aria-label="Menu"
          onClick={() => setMenuOpen((v) => !v)}>
          <span />
          <span />
          <span />
        </button>

        <h1 className="w777-login-logo">
          <span className="w777-logo-mark">{APP_BRAND_MARK}</span>
          <span className="w777-logo-tail">{APP_BRAND_TAIL}</span>
        </h1>

        {headerLogin ? <div className="w777-header-login">{headerLogin}</div> : null}

        <div className="w777-header-actions">
          <button type="button" className="w777-login-top-btn" onClick={onOpenLogin}>
            LOGIN
          </button>
          <button type="button" className="w777-demo-btn" onClick={onOpenLogin}>
            DEMO
          </button>
        </div>
      </header>

      <div className="w777-login-ticker">
        <img src={`${LOBBY_IMG}/speaker.svg`} alt="" className="w777-ticker-icon" />
        <div className="w777-ticker-track">
          <span>Sports &amp; Casino Action · Real-Time Odds · Live Casino · Instant Settlement · Welcome to {APP_BRAND_MARK} {APP_BRAND_TAIL}</span>
        </div>
      </div>

      <div className="w777-desk-shell">
        <aside className="w777-col-left">{leftRail}</aside>

        <main className="w777-lobby w777-col-center">
          <section className="w777-featured-scroll w777-mobile-only">
            {FEATURED_EVENTS.map((event) => (
              <button key={event.label} type="button" className={`w777-featured-chip ${event.tone}`}>
                <LobbySportIcon name={event.icon} className="w777-featured-icon" />
                <span>{event.label}</span>
              </button>
            ))}
          </section>

          <section className="w777-banner-wrap">
            <div
              className="w777-banner"
              style={{ backgroundImage: `url(${banners[bannerIdx]})` }}
              role="img"
              aria-label="Promotional banner"
            />
            <div className="w777-banner-dots">
              {banners.map((src, idx) => (
                <button
                  key={src}
                  type="button"
                  className={idx === bannerIdx ? "active" : ""}
                  aria-label={`Go to banner ${idx + 1}`}
                  onClick={() => setBannerIdx(idx)}
                />
              ))}
            </div>
          </section>

          <section className="w777-quick-tabs w777-mobile-only">
            {QUICK_TAB_ITEMS.map((tab) => (
              <button key={tab.label} type="button" className="w777-quick-tab-btn" onClick={onOpenLogin}>
                <LobbySportIcon name={tab.icon} className="w777-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </section>

          <section className="w777-sports-strip">
            {SPORT_STRIP_ITEMS.map((sport) => (
              <button
                key={sport.label}
                type="button"
                className={sport.label === activeSport.label ? "active" : ""}
                onClick={() => setActiveSport(sport)}>
                <LobbySportIcon name={sport.icon} className="w777-sport-icon" />
                <span>{sport.label}</span>
              </button>
            ))}
          </section>

          <section className="w777-match-board">
            <div className="w777-match-head">
              <span className="w777-match-sport">{activeSport.label}</span>
              <div className="w777-match-cols">
                <span>1</span>
                <span>X</span>
                <span>2</span>
              </div>
            </div>

            {!activeSport.sportId ? (
              <div className="w777-empty-row">Login to view {activeSport.label} markets</div>
            ) : matchesLoading && matches.length === 0 ? (
              <div className="w777-empty-row">Loading matches…</div>
            ) : matches.length === 0 ? (
              <div className="w777-empty-row">no records found</div>
            ) : (
              <div className="w777-match-list">
                {matches.map((m) => {
                  const suspended = m.oddsSuspended || !m.hasOdds;
                  const hasDraw = Number(m.drawBack) > 0 || Number(m.drawLay) > 0;
                  return (
                    <button
                      key={m.matchId}
                      type="button"
                      className={`w777-match-row ${m.inPlay ? "is-live" : ""}`}
                      onClick={onOpenLogin}>
                      <div className="w777-match-info">
                        <div className="w777-match-title">
                          {m.inPlay ? <em className="w777-live-tag">Live</em> : null}
                          <strong>{m.matchName}</strong>
                          <span className="w777-tv-tag" title="TV">
                            <TvIcon />
                          </span>
                        </div>
                        <p>
                          {m.league || activeSport.label}
                          {" · "}
                          {moment(m.openDate).format("DD MMM HH:mm")}
                        </p>
                      </div>
                      <div className="w777-match-odds">
                        <OddCell back={m.team1Back} lay={m.team1Lay} suspended={suspended} />
                        <OddCell
                          back={hasDraw ? m.drawBack : null}
                          lay={hasDraw ? m.drawLay : null}
                          suspended={suspended || !hasDraw}
                        />
                        <OddCell back={m.team2Back} lay={m.team2Lay} suspended={suspended} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="w777-section w777-mobile-only">
            <h4>Now Trending</h4>
            <div className="w777-slider w777-slider-scroll">{renderCards(trendingCards)}</div>
          </section>

          <section className="w777-section">
            <h4>Our Casino</h4>
            <div className="w777-slider w777-slider-scroll">{renderCards(casinoCards)}</div>
          </section>

          <section className="w777-section">
            <h4>New Games</h4>
            <div className="w777-slider w777-slider-scroll">{renderCards(newGameCards)}</div>
          </section>

          <section className="w777-section w777-mobile-only">
            <h4>Winner Announcement</h4>
            <div className="w777-winner-list">
              {WINNERS.map((w) => (
                <div className="w777-winner-row" key={w.name + w.time}>
                  <div>
                    <strong>{w.name}</strong>
                    <p>{w.time}</p>
                  </div>
                  <span>{w.amount}</span>
                </div>
              ))}
            </div>
          </section>

          {footer ? <div className="w777-lobby-footer">{footer}</div> : null}
        </main>

        <aside className="w777-col-right">
          <div className="w777-rail-block">
            <h4 className="w777-rail-title">Winner Announcement</h4>
            <div className="w777-winner-list">
              {WINNERS.map((w) => (
                <div className="w777-winner-row" key={`r-${w.name}-${w.time}`}>
                  <div>
                    <strong>{w.name}</strong>
                    <p>{w.time}</p>
                  </div>
                  <span>{w.amount}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="w777-rail-block">
            <h4 className="w777-rail-title">Now Trending</h4>
            <div className="w777-trend-stack">
              {trendingCards.map((card) => (
                <button
                  key={`t-${card.id || card.altText}-${card.imageUrl}`}
                  type="button"
                  className="w777-trend-card"
                  style={{ backgroundImage: `url(${card.imageUrl})` }}
                  aria-label={card.altText}
                  onClick={onOpenLogin}
                />
              ))}
            </div>
          </div>
        </aside>
      </div>

      {loginOpen ? (
        <div className="w777-login-modal-wrap" onMouseDown={onCloseLogin}>
          <div onMouseDown={(e) => e.stopPropagation()}>{loginModal}</div>
        </div>
      ) : null}
    </div>
  );
};

export default PreLoginLobby;
